import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  makeStyles,
  IconButton,
  Grid,
  Dialog,
  Tooltip,
  Menu,
  MenuItem,
  Slider,
  CircularProgress
} from '@material-ui/core';
import moment from 'moment';
import { useSelector } from 'react-redux';

// Icons
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PauseIcon from '@material-ui/icons/Pause';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import GetAppIcon from '@material-ui/icons/GetApp';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import VolumeOffIcon from '@material-ui/icons/VolumeOff';
import RefreshIcon from '@material-ui/icons/Refresh';

import { useRecordingList } from '../../../sdk/livekit/hooks/useRecordingList';
import { usePlaybackChannel } from '../../../sdk/livekit/hooks/usePlaybackChannel';

const RATE_OPTIONS = [0.5, 1, 1.5, 2];

function cameraNumFromMask(mask) {
  return ((mask ?? 1) & 2) && !((mask ?? 1) & 1) ? 2 : 1;
}

function camLabelFromMask(mask) {
  return ((mask ?? 1) & 2) && !((mask ?? 1) & 1) ? 'CAM 2' : 'CAM 1';
}

function fmtClock(sec) {
  if (!isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtDuration(ms) {
  if (!ms) return '0s';
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}

const useStyles = makeStyles(() => ({
  root: {
    padding: '24px 30px',
    height: 'calc(100vh - 66px)',
    overflowY: 'auto',
    backgroundColor: '#ffffff', 
    color: '#12202f', 
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#e4e6eb',
      borderRadius: '4px',
    },
  },
  hourSection: {
    marginBottom: '32px',
  },
  hourHeader: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#0066cc', 
    borderBottom: '1px solid #e4e6eb',
    paddingBottom: '8px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  clipCard: {
    backgroundColor: '#ffffff', 
    border: '1px solid #e4e6eb', 
    borderRadius: '10px',
    overflow: 'hidden',
    position: 'relative',
    height: '275px', 
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)', 
    transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
    '&:hover': {
      borderColor: '#0066cc',
      boxShadow: '0 8px 24px rgba(0, 102, 204, 0.12)',
      transform: 'translateY(-2px)',
    },
  },
  videoArea: {
    height: '140px',
    position: 'relative',
    backgroundColor: '#f0f2f5', 
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    overflow: 'hidden',
  },
  playOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.2s',
    '&:hover': {
      opacity: 1,
    },
  },
  cardTimeBadge: {
    position: 'absolute',
    top: '8px',
    left: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    color: '#12202f',
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '4px',
    zIndex: 2,
    border: '1px solid #e4e6eb',
  },
  cardDurationBadge: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    color: '#0066cc',
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '4px',
    zIndex: 2,
    border: '1px solid #e4e6eb',
  },
  cardDetails: {
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'space-between',
    backgroundColor: '#ffffff',
  },
  clipTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#12202f',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  clipSub: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '11px',
    color: '#65676b',
    marginTop: '2px',
  },
  cardControls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '6px',
    borderTop: '1px solid #e4e6eb',
    paddingTop: '6px',
  },
  iconBtn: {
    color: '#65676b',
    padding: '4px',
    transition: 'color 0.2s',
    '&:hover': {
      color: '#0066cc',
    },
  },
}));

const ClipCard = ({ clip, activeClipId, videoUrl, status, progress, buffering, onPlay, onPause, onFullscreen }) => {
  const classes = useStyles();
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(60);
  const [rateAnchor, setRateAnchor] = useState(null);
  const [isMuted, setIsMuted] = useState(true);

  const isCurrentActive = activeClipId === clip.id;

  
  useEffect(() => {
    if (isCurrentActive) {
      setPlaying(false);
      setCurrentTime(0);
      setDuration(clip.durationMs / 1000 || 60);
      if (videoRef.current && videoUrl) {
        videoRef.current.playbackRate = rate;
      }
    }
  }, [isCurrentActive, videoUrl, rate, clip]);

  const handlePlayToggle = (e) => {
    e.stopPropagation();
    if (isCurrentActive) {
      if (playing) {
        videoRef.current?.pause();
      } else {
        videoRef.current?.play().catch(() => {});
      }
    } else {
      onPlay(clip);
    }
  };

  const handleRateChange = (r) => {
    setRate(r);
    setRateAnchor(null);
    if (videoRef.current) {
      videoRef.current.playbackRate = r;
    }
  };

  const handleScreenshot = (e) => {
    e.stopPropagation();
    const videoEl = videoRef.current;
    if (!videoEl) return;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = videoEl.videoWidth || 640;
      canvas.height = videoEl.videoHeight || 360;
      canvas.getContext('2d').drawImage(videoEl, 0, 0, canvas.width, canvas.height);
      
      const link = document.createElement('a');
      link.download = `chup_${clip.timestamp.replace(/:/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      alert('Không thể chụp ảnh từ video.');
    }
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    if (videoUrl) {
      const link = document.createElement('a');
      link.href = videoUrl;
      link.download = `clip_event_slot_${clip.slot_id || 'vod'}.mp4`;
      link.click();
    }
  };

  const handleSeek = (e, newVal) => {
    setCurrentTime(newVal);
    if (videoRef.current) {
      videoRef.current.currentTime = newVal;
    }
  };

  return (
    <Box className={classes.clipCard}>
      <Box className={classes.videoArea} onClick={handlePlayToggle}>
        <span className={classes.cardTimeBadge}>{clip.timestamp}</span>
        <span className={classes.cardDurationBadge}>{clip.fileSize}</span>

        {isCurrentActive ? (
          <>
            {videoUrl ? (
              <video
                ref={videoRef}
                src={videoUrl || undefined}
                muted={isMuted}
                playsInline
                autoPlay
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                onEmptied={() => setPlaying(false)}
                onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
                onLoadedMetadata={(e) => {
                  if (isFinite(e.target.duration) && e.target.duration > 0) {
                    setDuration(e.target.duration);
                  }
                }}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" width="100%" bgcolor="#000" gap={1}>
                <CircularProgress size={24} style={{ color: '#58a6ff' }} />
                <Typography variant="caption" style={{ color: '#8b949e', fontSize: '11px', fontWeight: 600 }}>
                  {status === 'transferring' ? `Đang tải: ${Math.round(progress * 100)}%` : 'Đang kết nối...'}
                </Typography>
              </Box>
            )}
            {videoUrl && buffering && (
              <span className={classes.cardDurationBadge} style={{ top: 8, right: 8, color: '#d6b94a' }}>
                Tải nền {Math.round(progress * 100)}%
              </span>
            )}
          </>
        ) : (
          <>
            <img
              src="/static/iconSvg/video_storage.svg"
              alt="placeholder"
              style={{ width: '48px', height: '48px', opacity: 0.6 }}
            />
            <div className={classes.playOverlay}>
              <IconButton style={{ color: '#fff', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <PlayArrowIcon style={{ fontSize: 32 }} />
              </IconButton>
            </div>
          </>
        )}
      </Box>

      <Box className={classes.cardDetails}>
        <Box>
          <Typography className={classes.clipTitle} title={clip.fileName}>
            {clip.fileName}
          </Typography>
          <Box className={classes.clipSub}>
            <span>{clip.fileSize}</span>
            <span>{clip.cameraLabel}</span>
          </Box>
        </Box>

        {isCurrentActive && (
          <Box style={{ width: '100%', marginTop: '4px' }}>
            <Slider
              value={currentTime}
              min={0}
              max={duration}
              step={0.1}
              onChange={handleSeek}
              size="small"
              style={{ color: '#58a6ff', padding: '4px 0' }}
            />
          </Box>
        )}

        <Box className={classes.cardControls}>
          <IconButton onClick={handlePlayToggle} className={classes.iconBtn} size="small" disabled={isCurrentActive && !videoUrl}>
            {playing ? <PauseIcon style={{ fontSize: 18 }} /> : <PlayArrowIcon style={{ fontSize: 18 }} />}
          </IconButton>

          <IconButton onClick={(e) => { e.stopPropagation(); setIsMuted(!isMuted); }} className={classes.iconBtn} size="small">
            {isMuted ? <VolumeOffIcon style={{ fontSize: 18 }} /> : <VolumeUpIcon style={{ fontSize: 18 }} />}
          </IconButton>

          <Box>
            <IconButton onClick={(e) => { e.stopPropagation(); setRateAnchor(e.currentTarget); }} className={classes.iconBtn} style={{ fontSize: '11px', fontWeight: 700 }} size="small" disabled={!videoUrl}>
              {rate}x
            </IconButton>
            <Menu
              anchorEl={rateAnchor}
              open={Boolean(rateAnchor)}
              onClose={() => setRateAnchor(null)}
            >
              {RATE_OPTIONS.map(r => (
                <MenuItem key={r} onClick={() => handleRateChange(r)}>{r}x</MenuItem>
              ))}
            </Menu>
          </Box>

          <Tooltip title="Chụp ảnh">
            <IconButton onClick={handleScreenshot} className={classes.iconBtn} size="small" disabled={!videoUrl}>
              <CameraAltIcon style={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Tải xuống">
            <IconButton onClick={handleDownload} className={classes.iconBtn} size="small" disabled={!videoUrl}>
              <GetAppIcon style={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Toàn màn hình">
            <IconButton onClick={(e) => { e.stopPropagation(); onFullscreen(clip); }} className={classes.iconBtn} size="small" disabled={!videoUrl}>
              <FullscreenIcon style={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

const FullscreenPlayerDialog = ({ clip, videoUrl, status, progress, buffering, onClose }) => {
  const classes = useStyles();
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [rate, setRate] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(60);
  const [rateAnchor, setRateAnchor] = useState(null);
  const [volume, setVolume] = useState(100);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    if (videoRef.current && videoUrl) {
      videoRef.current.playbackRate = rate;
    }
  }, [videoUrl, rate]);

  const handlePlayToggle = () => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(() => {});
      }
    }
  };

  const handleRateChange = (r) => {
    setRate(r);
    setRateAnchor(null);
    if (videoRef.current) {
      videoRef.current.playbackRate = r;
    }
  };

  const handleScreenshot = () => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = videoEl.videoWidth || 1920;
      canvas.height = videoEl.videoHeight || 1080;
      canvas.getContext('2d').drawImage(videoEl, 0, 0, canvas.width, canvas.height);
      
      const link = document.createElement('a');
      link.download = `chup_fullscreen_${clip.timestamp.replace(/:/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      alert('Không thể chụp ảnh.');
    }
  };

  const handleDownload = () => {
    if (videoUrl) {
      const link = document.createElement('a');
      link.href = videoUrl;
      link.download = `clip_fullscreen_${clip.slot_id}.mp4`;
      link.click();
    }
  };

  const handleSeek = (e, newVal) => {
    setCurrentTime(newVal);
    if (videoRef.current) {
      videoRef.current.currentTime = newVal;
    }
  };

  const handleVolumeChange = (e, newVal) => {
    setVolume(newVal);
    if (videoRef.current) {
      videoRef.current.volume = newVal / 100;
      videoRef.current.muted = newVal === 0;
      setIsMuted(newVal === 0);
    }
  };

  const handleMuteToggle = () => {
    const nextMuted = !isMuted;
    setIsMuted(nextMuted);
    if (videoRef.current) {
      videoRef.current.muted = nextMuted;
    }
  };

  return (
    <Dialog
      fullScreen
      open={true}
      onClose={onClose}
      PaperProps={{
        style: {
          backgroundColor: '#000',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      <Box style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {videoUrl ? (
          <video
            ref={videoRef}
            src={videoUrl || undefined}
            muted={isMuted}
            playsInline
            autoPlay
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
            onEmptied={() => setPlaying(false)}
            onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
            onLoadedMetadata={(e) => {
              if (isFinite(e.target.duration) && e.target.duration > 0) {
                setDuration(e.target.duration);
              }
            }}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : (
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" width="100%" bgcolor="#000" gap={1}>
            <CircularProgress size={32} style={{ color: '#58a6ff' }} />
            <Typography style={{ color: '#8b949e', fontSize: '13px' }}>
              {status === 'transferring' ? `Đang tải: ${Math.round(progress * 100)}%` : 'Đang kết nối...'}
            </Typography>
          </Box>
        )}

        {videoUrl && buffering && (
          <Box style={{
            position: 'absolute', top: 20, right: 20, padding: '4px 12px',
            borderRadius: 20, backgroundColor: 'rgba(22, 27, 34, 0.85)',
            color: '#d6b94a', fontWeight: 700, fontSize: 12,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span>⬇ Tải nền {Math.round(progress * 100)}%</span>
            <span style={{ width: 60, height: 4, backgroundColor: '#30363d', borderRadius: 2, display: 'inline-block', overflow: 'hidden' }}>
              <span style={{ display: 'block', width: `${Math.round(progress * 100)}%`, height: '100%', backgroundColor: '#d6b94a' }} />
            </span>
          </Box>
        )}
      </Box>

      <Box style={{
        padding: '16px 24px',
        backgroundColor: '#161b22',
        borderTop: '1px solid #30363d',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        <Box style={{ width: '100%' }}>
          <Slider
            value={currentTime}
            min={0}
            max={duration}
            step={0.1}
            onChange={handleSeek}
            disabled={!videoUrl}
            style={{ color: '#58a6ff' }}
          />
        </Box>
        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <IconButton onClick={handlePlayToggle} style={{ color: '#f0f6fc', padding: 8 }} disabled={!videoUrl}>
              {playing ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>

            <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <IconButton onClick={handleMuteToggle} style={{ color: '#f0f6fc', padding: 8 }} disabled={!videoUrl}>
                {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
              </IconButton>
              <Slider
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                min={0}
                max={100}
                disabled={!videoUrl}
                style={{ width: '100px', color: '#f0f6fc' }}
              />
            </Box>

            <Typography style={{ color: '#8b949e', fontSize: '13px', fontFamily: 'monospace' }}>
              {fmtClock(currentTime)} / {fmtClock(duration)}
            </Typography>
          </Box>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Box>
              <IconButton onClick={(e) => setRateAnchor(e.currentTarget)} style={{ color: '#f0f6fc', fontSize: '14px', fontWeight: 600 }} disabled={!videoUrl}>
                {rate}x
              </IconButton>
              <Menu
                anchorEl={rateAnchor}
                open={Boolean(rateAnchor)}
                onClose={() => setRateAnchor(null)}
              >
                {RATE_OPTIONS.map(r => (
                  <MenuItem key={r} onClick={() => handleRateChange(r)}>{r}x</MenuItem>
                ))}
              </Menu>
            </Box>

            <Tooltip title="Chụp ảnh màn hình">
              <IconButton onClick={handleScreenshot} style={{ color: '#f0f6fc' }} disabled={!videoUrl}>
                <CameraAltIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Tải xuống video">
              <IconButton onClick={handleDownload} style={{ color: '#f0f6fc' }} disabled={!videoUrl}>
                <GetAppIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Thu nhỏ">
              <IconButton onClick={onClose} style={{ color: '#f85149' }}>
                <FullscreenExitIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default function PlaybackTab({ isSidebarOpen, isShowResult }) {
  const classes = useStyles();
  const [activeClipId, setActiveClipId] = useState(null);
  const [fullscreenClip, setFullscreenClip] = useState(null);

  const vehicleSelected = useSelector(state => state.vodSlice?.vehicleSelected);
  
  const deviceSerial = vehicleSelected?.deviceSerial || vehicleSelected?.serial || '';
  // Channel id is CMS-side convention: vehicleSelected.channelId is 1|2|3|4
  // for the 4-camera setup; firmware addresses each as "<camIdx>01" (HD).
  const channelId = vehicleSelected?.channelId
    ? `${vehicleSelected.channelId}01`
    : '101';

  const [sinceMs, setSinceMs] = useState(() => Date.now() - 24 * 3600 * 1000);
  const [untilMs, setUntilMs] = useState(() => Date.now());

  const { recordings, loading: listLoading, error: listError, refresh } = useRecordingList(isShowResult ? deviceSerial : '');
  const { status, error: pbError, videoUrl, progress, durationMs, buffering, requestPlayback } = usePlaybackChannel(isShowResult ? deviceSerial : '', channelId);

  useEffect(() => {
    if (isShowResult && vehicleSelected && deviceSerial) {
      const start = vehicleSelected.first_time ? vehicleSelected.first_time * 1000 : Date.now() - 24 * 3600 * 1000;
      const end = vehicleSelected.last_time ? vehicleSelected.last_time * 1000 : Date.now();

      setSinceMs(start);
      setUntilMs(end);

      refresh({
        sinceMs: start,
        untilMs: end
      });
    }
  }, [isShowResult, vehicleSelected, deviceSerial, refresh]);

  const handleRefresh = useCallback(() => {
    refresh({ sinceMs, untilMs });
  }, [refresh, sinceMs, untilMs]);

  const handlePlayClip = (clip) => {
    setActiveClipId(clip.id);
    if (clip.slot_id != null) {
      requestPlayback(clip.slot_id, clip.cameraNum);
    }
  };

  const handlePauseClip = () => {
    setActiveClipId(null);
  };

  const clips = (recordings || []).map((ev, idx) => {
    const startTsMs = ev.tsUs / 1000;
    const duration = ev.durationMs ?? ((ev.preRollMs ?? 0) + (ev.postRollMs ?? 0));
    const timestamp = moment(startTsMs).format('HH:mm:ss');
    const hour = moment(startTsMs).hour();
    const cameraNum = cameraNumFromMask(ev.cameraMask);
    const cameraLabel = camLabelFromMask(ev.cameraMask);
    
    return {
      id: ev.slotId != null ? `${ev.slotId}_${ev.tsUs}` : `clip_${idx}`,
      slot_id: ev.slotId,
      ts_us: ev.tsUs,
      startTsMs,
      durationMs: duration,
      timestamp,
      hour,
      cameraNum,
      cameraLabel,
      fileSize: fmtDuration(duration),
      fileName: `Lưu trữ thiết bị - ${cameraLabel} - ${moment(startTsMs).format('YYYY-MM-DD HH:mm:ss')}`,
      type: ev.type || 'recording',
    };
  });

  const groupClipsByHour = (clipsList) => {
    const groups = {};
    clipsList.forEach(clip => {
      const h = clip.hour;
      if (!groups[h]) {
        groups[h] = [];
      }
      groups[h].push(clip);
    });
    return groups;
  };
  
  const groupedClips = groupClipsByHour(clips);
  const sortedHours = Object.keys(groupedClips).sort((a, b) => Number(a) - Number(b));

  if (listLoading) {
    return (
      <Box className={classes.root} display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" gap={2}>
        <CircularProgress style={{ color: '#0066cc' }} />
      </Box>
    );
  }

  if (!vehicleSelected || !isShowResult) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        style={{ height: '60vh', marginTop: '24px' }}
      >
        <Typography style={{ color: '#D9042F', fontSize: '16px', fontWeight: 500, textAlign: 'center' }}>
          Vui lòng chọn biển số xe để tìm kiếm video.
        </Typography>
      </Box>
    );
  }

  if (listError) {
    return (
      <Box className={classes.root} display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" gap={2}>
        <Typography style={{ color: '#d32f2f', fontSize: '15px', fontWeight: 600 }}>
          Lỗi kết nối thiết bị: {listError}
        </Typography>
        <IconButton onClick={handleRefresh} style={{ color: '#0066cc' }}>
          <RefreshIcon />
        </IconButton>
      </Box>
    );
  }

  if (clips.length === 0) {
    return (
      <Box className={classes.root} display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography style={{ color: '#d32f2f', fontSize: '15px', fontWeight: 500 }}>
          Không tìm thấy video nào ghi trên thẻ nhớ trong khoảng thời gian này.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {pbError && (
        <Box style={{ marginBottom: '16px', padding: '10px 14px', borderRadius: '6px', backgroundColor: '#ffebe9', color: '#d32f2f', fontSize: '12px', fontWeight: 600, border: '1px solid #ffc8c2' }}>
          ⚠ Lỗi phát lại: {pbError}
        </Box>
      )}

      {sortedHours.map(hour => {
        const clipsInHour = groupedClips[hour];
        return (
          <Box className={classes.hourSection} key={hour}>
            <Typography className={classes.hourHeader}>
              {`${hour.padStart(2, '0')}:00 - ${(Number(hour) + 1).toString().padStart(2, '0')}:00 (${clipsInHour.length} video)`}
            </Typography>
            <Grid container spacing={3}>
              {clipsInHour.map(clip => (
                <Grid item key={clip.id} lg={3} md={4} sm={6} xs={12}>
                  <ClipCard
                    clip={clip}
                    activeClipId={activeClipId}
                    videoUrl={videoUrl}
                    status={status}
                    progress={progress}
                    buffering={buffering}
                    onPlay={handlePlayClip}
                    onPause={handlePauseClip}
                    onFullscreen={(c) => setFullscreenClip(c)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}

      {fullscreenClip && (
        <FullscreenPlayerDialog
          clip={fullscreenClip}
          videoUrl={videoUrl}
          status={status}
          progress={progress}
          buffering={buffering}
          onClose={() => setFullscreenClip(null)}
        />
      )}
    </Box>
  );
}
